import * as React from 'react'
import { useState, useEffect } from 'react'

interface Region {
  id: number
  name: string
  type: 'Regions::Region'
}

interface District {
  id: number
  name: string
  parent_id: number
  type: 'Regions::District'
}

interface RegionDistrictSelectorProps {
  regions: Region[]
  districts: District[]
  selectedValue?: number
  fieldName: string
  regionLabel?: string
  districtLabel?: string
}

const RegionDistrictSelector: React.FC<RegionDistrictSelectorProps> = ({
  regions,
  districts,
  selectedValue,
  fieldName,
  regionLabel = 'Region',
  districtLabel = 'District',
}) => {
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null)
  const [fieldValue, setFieldValue] = useState<number | null>(selectedValue || null)

  // Initialize state based on selectedValue
  useEffect(() => {
    if (selectedValue) {
      const selectedDistrict = districts.find((d) => d.id === selectedValue)
      if (selectedDistrict) {
        setSelectedRegionId(selectedDistrict.parent_id)
        setSelectedDistrictId(selectedValue)
        setFieldValue(selectedValue)
      } else {
        const selectedRegion = regions.find((r) => r.id === selectedValue)
        if (selectedRegion) {
          setSelectedRegionId(selectedValue)
          setSelectedDistrictId(null)
          setFieldValue(selectedValue)
        }
      }
    }
  }, [selectedValue, regions, districts])

  // Get districts for selected region
  const availableDistricts = selectedRegionId
    ? districts.filter((district) => district.parent_id === selectedRegionId)
    : []

  // Check if company has any districts
  const hasDistricts = districts && districts.length > 0

  const handleRegionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const regionId = event.target.value ? parseInt(event.target.value) : null
    setSelectedRegionId(regionId)
    setSelectedDistrictId(null) // Reset district selection
    setFieldValue(regionId) // Set field to region ID
  }

  const handleDistrictChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const districtId = event.target.value ? parseInt(event.target.value) : null
    setSelectedDistrictId(districtId)
    // If district is selected, use district ID, otherwise fall back to region ID
    setFieldValue(districtId || selectedRegionId)
  }

  return (
    <>
      {/* Hidden field that stores the actual value */}
      <input type="hidden" name={fieldName} value={fieldValue || ''} />

      {/* Region selector */}
      <li className="select input form-group optional" id="location_region_input">
        <label htmlFor="region_selector" className="label">
          {regionLabel}
        </label>
        <div className="default-form-input-wrapper">
          <select
            id="region_selector"
            className="form-control"
            value={selectedRegionId || ''}
            onChange={handleRegionChange}
          >
            <option value="" label=" "></option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>
      </li>

      {/* District selector - only show if company has districts */}
      {hasDistricts && (
        <li className="select input form-group optional" id="location_district_input">
          <label htmlFor="district_selector" className="label">
            {districtLabel}
          </label>
          <div className="default-form-input-wrapper">
            <select
              id="district_selector"
              className="form-control"
              value={selectedDistrictId || ''}
              onChange={handleDistrictChange}
              disabled={!selectedRegionId || availableDistricts.length === 0}
            >
              <option value="" label=" "></option>
              {availableDistricts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </select>
          </div>
        </li>
      )}
    </>
  )
}

export default RegionDistrictSelector
